import Tabbable from 'tabbable'
import Component from '../../lib/component'

export default class DatepickerComponent extends Component {
    static selector () {
        return '.datepicker'
    }

    constructor (opts) {
        super(opts)

        this.focusOnRender = null

        if (this.props.minDate)
            this.props.minDateInit = this.props.minDate

        if (this.props.maxDate)
            this.props.maxDateInit = this.props.maxDate

        this.render()
        this.listen()
    }

    listen () {
        this.getDatepickerInput()
            .addEventListener('keydown', this.onInputKeyDown.bind(this))

        this.getDatepickerInput()
            .addEventListener('change', this.onChange.bind(this))

        if ('MutationObserver' in window)
            (new window
                .MutationObserver((mutations) => this.render()))
                .observe(this.el, {
                    attributes: true
                })

        if (this.getFeature('touch'))
            return

        document.addEventListener('keydown', (e) =>
            this.isDatepickerCalendarExpanded()
                ? this.onDatepickerCalendarToggle(e)
                : null)

        document.addEventListener('click', (e) =>
            this.isDatepickerCalendarExpanded()
                    && this.getDatepickerContainer()
                    && !(this.getDatepickerContainer().contains(e.target)
                        || e.target === this.getDatepickerContainer())
                ? this.onDatepickerCalendarToggle(e)
                : null)

        this.getDatepickerInput()
            .addEventListener('click',
                this.onDatepickerCalendarToggle.bind(this))

        if (this.getDatepickerIconBtn())
            this.getDatepickerIconBtn()
                .addEventListener('click',
                    this.onDatepickerCalendarToggle.bind(this))

        if (this.getDatepickerCalendarBtnNext())
            this.getDatepickerCalendarBtnNext()
                .addEventListener('click',
                    this.onDatepickerCalendarBtnClick.bind(this))

        if (this.getDatepickerCalendarBtnPrev())
            this.getDatepickerCalendarBtnPrev()
                .addEventListener('click',
                    this.onDatepickerCalendarBtnClick.bind(this))

        if (!this.getDatepickerCalendar())
            return

        this.getDatepickerCalendar()
            .addEventListener('keydown',
                this.onDatepickerCalendarKeyDown.bind(this))

        const tableBody = this.getDatepickerCalendar()
            .querySelector('table tbody')

        if (tableBody)
            tableBody.addEventListener('click',
                this.onDatepickerCalendarDateClick.bind(this))
    }

    emitChange () {
        let evt

        try {
            evt = new window.Event('change', { view: window, bubbles: true })
        }
        catch (e) {
            evt = document.createEvent('Event')
            evt.initEvent('change', true, true)
        }

        this.getDatepickerInput()
            .dispatchEvent(evt)
    }

    onChange (e) {
        let date = this.getDate(e.target.value)

        const isValidDate = this.isValidDate(date)

        const maxDate = this.getMaxDate()

        const minDate = this.getMinDate()

        if (date && isValidDate) {
            if (minDate
                    && minDate.getTime() > date.getTime())
                date = minDate

            if (maxDate
                    && maxDate.getTime() < date.getTime())
                date = maxDate

            if (e.target.value !== this.getDateTimestamp(date))
                this.getDatepickerInput().value = this.getDateTimestamp(date)
        }

        if (date && !isValidDate) {
            date = this.getTodayDate()

            if (minDate
                    && minDate.getTime() > date.getTime())
                date = minDate

            if (maxDate
                    && maxDate.getTime() < date.getTime())
                date = maxDate

            this.getDatepickerInput().value
                = this.getDateTimestamp(date)
        }

        this.render()
    }

    onInputKeyDown (e) {
        const key = (e.key || e.keyIdentifier || '')

        if (key.match(/(?:arrow)*down$/i)) {
            e.preventDefault()
            e.stopPropagation()

            const inputDate = this.getDate(this.getDatepickerInput().value)

            if (this.isValidDate(inputDate)
                    && this.props.calendar
                        !== this.getDateTimestamp(inputDate, false))
                this.props.calendar = this.getDateTimestamp(inputDate, false)

            this.onDatepickerCalendarToggle(e)
            this.emitChange()

            const date = this.getDatepickerCalendar()
                ? this.getDatepickerCalendar()
                    .querySelector('td[aria-selected="true"]')
                        || this.getDatepickerCalendar()
                            .querySelector('td.today')
                        || this.getDatepickerCalendar()
                            .querySelector('td:not(.disabled)')
                : null

            if (date)
                Array.from(this.getDatepickerCalendar()
                    .querySelectorAll('td[tabindex="0"]'))
                    .forEach(el => el !== date
                        && el.setAttribute('tabindex', -1))

            if (date && date.getAttribute('tabindex') !== '0')
                date.setAttribute('tabindex', 0)

            return date
                ? date.focus()
                : null
        }

        if (key.match(/tab/i))
            return this.emitChange()

        if (!key.match(/^[a-z]$/i) || e.ctrlKey)
            return

        e.preventDefault()
        e.stopPropagation()
    }

    onDatepickerCalendarKeyDown (e) {
        const key = (e.key || e.keyIdentifier || '')

        if (key.match(/tab/i)) {
            let activeEl

            const els = (Tabbable(this.getDatepickerCalendar()) || [])

            if (e.shiftKey
                    && els.indexOf(e.target) === 0)
                activeEl = els[els.length - 1]

            if (!e.shiftKey
                    && els.indexOf(e.target) === els.length - 1)
                activeEl = els[0]

            if (!activeEl)
                return

            e.preventDefault()
            e.stopPropagation()

            return activeEl.focus()
        }

        if ((key.match(/^(enter)$/i) || e.keyCode === 32)
                && e.target.tagName === 'TD') {
            e.preventDefault()
            e.stopPropagation()

            return this.onDatepickerCalendarDateClick(e)
        }

        if (!key.match(/(up|down|left|right|home|end)$/i))
            return

        e.preventDefault()
        e.stopPropagation()

        const currMonth = this.getDate(this.props.calendar)

        const prevMonth = new Date(currMonth.getFullYear(),
            currMonth.getMonth(), 0)

        const nextMonth = new Date(currMonth.getFullYear(),
            currMonth.getMonth() + 1, 1)

        const currDate = this.getDatepickerCalendar()
            .querySelector('td[tabindex="0"]')

        if (!currDate)
            return

        const currWeek = currDate.parentElement

        const currWeekDatesAll = Array.from(currWeek.children)

        const currWeekDates = currWeekDatesAll
            .filter(el => !el.classList.contains('disabled'))

        const prevWeek = currWeek.previousElementSibling

        const prevWeekDatesAll = prevWeek
            ? Array.from(prevWeek.children)
            : []

        const prevWeekDates = prevWeekDatesAll
            .filter(el => !el.classList.contains('disabled'))

        const nextWeek = currWeek.nextElementSibling

        const nextWeekDatesAll = nextWeek
            ? Array.from(nextWeek.children)
            : []

        const nextWeekDates = nextWeekDatesAll
            .filter(el => !el.classList.contains('disabled'))

        let date

        if (key.match(/(home|end)$/i))
            date = key.match(/home/i)
                ? currWeekDates[0]
                : currWeekDates[currWeekDates.length - 1]

        // Left Arrow
        const isLeft = key.match(/(?:arrow)*left/i)

        if (isLeft && currWeekDates[currWeekDates.indexOf(currDate) - 1])
            date = currWeekDates[currWeekDates.indexOf(currDate) - 1]

        if (isLeft
                && currWeekDates[0] === e.target
                && prevWeekDates[prevWeekDates.length - 1])
            date = prevWeekDates[prevWeekDates.length - 1]

        // Right Arrow
        const isRight = key.match(/(?:arrow)*right/i)

        if (isRight
                && currWeekDates[currWeekDates.length - 1] === e.target
                && nextWeekDates[0])
            date = nextWeekDates[0]

        if (isRight
                && currWeekDates[currWeekDates.indexOf(currDate) + 1])
            date = currWeekDates[currWeekDates.indexOf(currDate) + 1]

        const currWeekDayIdx = currWeekDatesAll.indexOf(currDate)

        // Up Arrow
        const isUp = key.match(/(?:arrow)*up/i)

        if (isUp
                && prevWeekDatesAll[currWeekDayIdx]
                && !prevWeekDatesAll[currWeekDayIdx].classList
                    .contains('disabled'))
            date = prevWeekDatesAll[currWeekDayIdx]

        // Down Arrow
        const isDown = key.match(/(?:arrow)*down/i)

        if (isDown
                && nextWeekDatesAll[currWeekDayIdx]
                && !nextWeekDatesAll[currWeekDayIdx].classList
                    .contains('disabled'))
            date = nextWeekDatesAll[currWeekDayIdx]

        const isPrevMonthEnabled = this.getDatepickerCalendarBtnPrev()
            && !this.getDatepickerCalendarBtnPrev().disabled

        const isPageUp = key.match(/^pageup$/i)

        // Go-to Prev Month
        const isPrevMonth = isPrevMonthEnabled
            && (isPageUp
                || (isLeft
                    && !date
                    && !prevWeek)
                || (isUp
                    && !date))

        const isNextMonthEnabled = this.getDatepickerCalendarBtnNext()
            && !this.getDatepickerCalendarBtnNext().disabled

        const isPageDown = key.match(/^pagedown$/i)

        // Go-to Next Month
        const isNextMonth = isNextMonthEnabled
            && (isPageDown
                || (isRight
                    && !date
                    && !nextWeek)
                || (isDown
                    && !date))

        if ((isPrevMonth || isNextMonth)
                && (isLeft || isRight))
            this.focusOnRender = `td[data-date="${
                this.getDateTimestamp(isPrevMonth
                    ? prevMonth
                    : nextMonth)}"]`

        if ((isPrevMonth || isNextMonth)
                && (isUp || isPageUp || isDown || isPageDown))
            this.focusOnRender = () => {
                let rows = Array
                    .from(this.getDatepickerCalendar()
                        .querySelectorAll('tbody tr'))

                if (isUp || isPageUp)
                    rows = rows.reverse()

                const cell = rows
                    .filter(el => Array.from(el.children)[currWeekDayIdx]
                        && !Array.from(el.children)[currWeekDayIdx]
                            .classList.contains('disabled'))
                    .map(el => Array.from(el.children)[currWeekDayIdx])[0]

                return cell || this.getDatepickerCalendar()
                    .querySelector(`td[data-date="${
                        this.getDateTimestamp(isPrevMonth
                            ? prevMonth
                            : nextMonth)}"]`)
            }

        if (isPrevMonth || isNextMonth)
            this.props.calendar = this.getDateTimestamp(isPrevMonth
                ? prevMonth
                : nextMonth, false)

        if (!date || currDate === date)
            return

        Array.from(this.getDatepickerCalendar()
            .querySelectorAll('td[tabindex="0"]'))
            .forEach(el => el.setAttribute('tabindex', -1))

        if (date && date.getAttribute('tabindex') !== '0')
            date.setAttribute('tabindex', 0)

        return date
            ? date.focus()
            : null
    }

    onDatepickerCalendarToggle (e) {
        if (!this.getDatepickerCalendar())
            return

        const key = (e.key || e.keyIdentifier || '')

        const isExpand = !this.isDatepickerCalendarExpanded()

        const isEscape = key
            && e.keyCode === 27
            && !isExpand

        const isToggle = !key
            || isEscape
            || (key.match(/(?:arrow)*down$/i)
               && isExpand)
            || ((key.match(/^(enter)$/i) || e.keyCode === 32)
                && e.target.tagName === 'TD'
                && !isExpand)

        if (!isToggle)
            return

        if (isEscape
                && this.el.contains(document.activeElement))
            this.getDatepickerInput()
                .focus()

        if (isExpand
                && e.target === this.getDatepickerIconBtn())
            this.focusOnRender = 'td[tabindex="0"]'

        this.getDatepickerInput()
            .setAttribute('aria-expanded', isExpand)

        this.el.classList[isExpand ? 'add' : 'remove']('active')

        if (!isExpand)
            this.props.calendar = ''
    }

    onDatepickerCalendarDateClick (e) {
        const isSelect = (e.type !== 'click'
                || (e.type === 'click'
                    && !e.target.getAttribute('tabindex')))
            && e.target.getAttribute('data-date')
            && !e.target.classList.contains('disabled')

        if (!isSelect)
            return

        const td = e.target.tagName === 'TD'
            ? e.target
            : e.target.parentElement

        const selected = this.getDatepickerCalendar()
            .querySelector('td[aria-selected="true"]')

        if (selected === td)
            return this.onDatepickerCalendarToggle(e)

        if (selected) {
            selected.removeAttribute('aria-selected')
            selected.setAttribute('tabindex', '-1')
        }

        td.setAttribute('aria-selected', true)
        td.setAttribute('tabindex', 0)

        this.getDatepickerInput().value = e.target
            .getAttribute('data-date')

        this.onDatepickerCalendarToggle(e)

        this.emitChange()

        this.getDatepickerInput()
            .focus()
    }

    onDatepickerCalendarBtnClick (e) {
        e.preventDefault()
        e.stopPropagation()

        const currMonth = this.getDate(this.props.calendar)

        const isNext = e.target === this.getDatepickerCalendarBtnNext()
            || this.getDatepickerCalendarBtnNext()
                .contains(e.target)

        this.props.calendar = this.getDateTimestamp(new Date(
            currMonth.getFullYear(),
            currMonth.getMonth() + (isNext
                ? 1
                : 0),
            isNext
                ? 1
                : 0)
        , false)
    }

    isDatepickerCalendarExpanded () {
        return this.getDatepickerInput()
            .getAttribute('aria-expanded')
                === 'true'
    }

    isRangeStart () {
        const input = this.getDatepickerRangeStartInput()

        return input
            && this.getDatepickerInput() === input
    }

    isRangeEnd () {
        const input = this.getDatepickerRangeEndInput()

        return input
            && this.getDatepickerInput() === input
    }

    isValidDate (date) {
        return date
                && date instanceof Date
                && !isNaN(date)
            ? date
            : null
    }

    getDate (str) {
        let date = str
            ? new Date(`${str}T00:00:00`)
            : null

        if (!this.isValidDate(date)
                || !(date.getHours()
                        && !date.getMinutes()
                        && !date.getSeconds()))
            return date

        date = new Date(date.setMinutes(date.getTimezoneOffset()))

        return date
    }

    getDatepickerContainer () {
        return this.el.querySelector('.datepicker-container')
    }

    getDatepickerCalendar () {
        return this.el.querySelector('.datepicker-calendar')
    }

    getDatepickerInput () {
        return this.el.querySelector('.datepicker-input')
    }

    getDateTimestamp (date, day = true) {
        if (!date
                || !(date instanceof Date)
                || isNaN(date))
            return ''

        return `${date.getFullYear()}-${
            String(date.getMonth() + 1).padStart(2, '0')}${
            day
                ? `-${String(date.getDate()).padStart(2, '0')}`
                : ''}`
    }

    getDatepickerRangeStartInput () {
        return this.props.datepickerRangeStartInputSelector
                && document.querySelector(
                    this.props.datepickerRangeStartInputSelector)
            ? document.querySelector(
                this.props.datepickerRangeStartInputSelector)
            : null
    }

    getDatepickerRangeEndInput () {
        return this.props.datepickerRangeEndInputSelector
                && document.querySelector(
                    this.props.datepickerRangeEndInputSelector)
            ? document.querySelector(
                this.props.datepickerRangeEndInputSelector)
            : null
    }

    getDatepickerTouchPlaceholder () {
        return this.el.querySelector('.datepicker-placeholder')
    }

    getDatepickerIconBtn () {
        return this.el.querySelector('button.datepicker-icon')
    }

    getDatepickerCalendarBtnNext () {
        return this.el.querySelector('.datepicker-calendar-btn-next')
    }

    getDatepickerCalendarBtnPrev () {
        return this.el.querySelector('.datepicker-calendar-btn-prev')
    }

    getMaxDate () {
        return this.props.maxDate
            ? (this.props.maxDate.match(/^(today|now)$/)
                ? this.getTodayDate()
                : this.getDate(this.props.maxDate))
            : null
    }

    getMinDate () {
        return this.props.minDate
            ? (this.props.minDate.match(/^(today|now)$/)
                ? this.getTodayDate()
                : this.getDate(this.props.minDate))
            : null
    }

    getTodayDate () {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return today
    }

    renderIcon () {
        let icon = this.el.querySelector('.datepicker-icon')

        if (!this.getFeature('touch')
                && icon
                && this.getDatepickerInput().disabled
                    !== icon.disabled)
            icon.disabled = this.getDatepickerInput().disabled

        if (icon)
            return

        icon = document.createElement(this.getFeature('touch')
            ? 'span'
            : 'button')

        icon.className = `datepicker-icon${!this.getFeature('touch')
            ? ' btn-unstyled'
            : ''}`

        icon.style.height = `${this.getDatepickerInput().clientHeight}px`
        icon.innerHTML
            = '<i class="material-icons" aria-hidden="true">expand_more</i>'

        if (!this.getFeature('touch')) {
            icon.setAttribute('role', 'button')
            icon.setAttribute('aria-label', 'Select date')
            icon.setAttribute('tabindex', -1)
        }

        if (this.getDatepickerContainer())
            this.getDatepickerContainer()
                .appendChild(icon)
    }

    renderTouchPlaceholder () {
        if (this.getDatepickerTouchPlaceholder())
            return

        const placeholder = this.props.datepickerTouchPlaceholder
            || 'Select date'

        if (!placeholder)
            return

        const span = document.createElement('span')
        span.className = 'datepicker-placeholder'
        span.style.height = `${this.getDatepickerInput().clientHeight}px`
        span.innerHTML = placeholder
        span.setAttribute('aria-hidden', true)

        if (this.getDatepickerContainer())
            this.getDatepickerContainer()
                .appendChild(span)
    }

    renderCalendar () {
        if (this.getFeature('touch'))
            return

        let calendar = this.getDatepickerCalendar()

        if (!calendar) {
            calendar = document.createElement('div')

            calendar.id = `datepicker-calendar-${
                this.getDatepickerInput().id || this.generateGUID()}`

            calendar.className = 'datepicker-calendar'

            calendar.setAttribute('role', 'dialog')
            calendar.setAttribute('aria-modal', 'true')
            calendar.setAttribute('aria-label', 'Select date')

            calendar.innerHTML = `
                <div class="datepicker-calendar-header row">
                    <div class="col-auto display-inline-flex">
                        <button
                            role="button"
                            class="btn-unstyled datepicker-calendar-btn datepicker-calendar-btn-prev"
                            aria-label="Previous month"
                        >
                            <i class="material-icons" aria-hidden="true">chevron_left</i>
                        </button>
                    </div>
                    <div class="col-auto">
                        <h2
                            id="${this.getDatepickerInput().id}-header-label"
                            class="datepicker-calendar-header-label body-2 font-weight-medium"
                            aria-live="polite"
                        ></h2>
                    </div>
                    <div class="col-auto display-inline-flex">
                        <button
                            role="button"
                            class="btn-unstyled datepicker-calendar-btn datepicker-calendar-btn-next"
                            aria-label="Next month"
                        >
                            <i class="material-icons" aria-hidden="true">chevron_right</i>
                        </button>
                    </div>
                </div>
                <table
                    class="datepicker-calendar-dates"
                    role="grid"
                    aria-labelledby="${this.getDatepickerInput().id}-header-label"
                >
                    <thead>
                        <tr>
                            <th scope="col" abbr="Sunday">Su</th>
                            <th scope="col" abbr="Monday">Mo</th>
                            <th scope="col" abbr="Tuesday">Tu</th>
                            <th scope="col" abbr="Wednesday">We</th>
                            <th scope="col" abbr="Thursday">Th</th>
                            <th scope="col" abbr="Friday">Fr</th>
                            <th scope="col" abbr="Saturday">Sa</th>
                        </tr>
                    </thead>
                    <tbody></tbody>
                </table>`

            if (this.getDatepickerContainer())
                this.getDatepickerContainer()
                    .appendChild(calendar)

            this.getDatepickerInput()
                .setAttribute('role', 'combobox')

            this.getDatepickerInput()
                .setAttribute('aria-haspopup', 'dialog')

            this.getDatepickerInput()
                .setAttribute('aria-expanded', false)

            this.getDatepickerInput()
                .setAttribute('aria-controls', calendar.id)
        }

        const top = `${this.getDatepickerInput().clientHeight}px`

        if (calendar.style.top !== top)
            calendar.style.top = top

        let date

        const maxDate = this.getMaxDate()
        const minDate = this.getMinDate()

        const inputValue = this.getDatepickerInput().value
        const inputValueDate = this.getDate(inputValue)

        if (!this.props.calendar) {
            date = inputValueDate || this.getTodayDate()

            if (minDate
                    && minDate.getTime() > date.getTime())
                date = minDate

            if (maxDate
                    && maxDate.getTime() < date.getTime())
                date = maxDate

            this.props.calendar = this.getDateTimestamp(date, false)
        }

        if (calendar.getAttribute('data-calendar') === this.props.calendar) {
            date = this.getDate(this.props.calendar)

            const isMinDateMonth = minDate
                && minDate.getMonth() === date.getMonth()

            const isMaxDateMonth = maxDate
                && maxDate.getMonth() === date.getMonth()

            if (!isMinDateMonth && !isMaxDateMonth)
                return

            return Array.from(this.getDatepickerCalendar()
                .querySelectorAll('td'))
                .filter(el => el.getAttribute('data-date'))
                .forEach(el => {
                    const day = this.getDate(el.getAttribute('data-date'))

                    const isDisabled = ((minDate
                            && minDate.getTime() > day.getTime())
                        || (maxDate
                            && maxDate.getTime() < day.getTime()))

                    if (isDisabled
                            && !el.classList.contains('disabled')) {
                        el.classList.add('disabled')

                        if (el.children[0])
                            el.children[0].classList.add('disabled')
                    }

                    if (!isDisabled
                            && el.classList.contains('disabled')) {
                        el.classList.remove('disabled')

                        if (el.children[0])
                            el.children[0].classList.remove('disabled')
                    }
                })
        }

        calendar.setAttribute('data-calendar', this.props.calendar)

        date = date || this.getDate(this.props.calendar)

        const year = date.getFullYear()

        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ]

        const prevBtn = this.getDatepickerCalendarBtnPrev()
        const nextBtn = this.getDatepickerCalendarBtnNext()

        if (prevBtn) {
            const prevMonth = new Date(year, date.getMonth(), 0)

            if ((!minDate
                        && prevBtn.disabled)
                    || (minDate
                        && minDate.getTime() < prevMonth.getTime()))
                prevBtn.disabled = false

            if (minDate
                    && minDate.getTime() > prevMonth.getTime())
                prevBtn.disabled = true

            if (prevBtn.disabled
                    && prevBtn.getAttribute('title'))
                prevBtn.removeAttribute('title')

            if (!prevBtn.disabled)
                prevBtn.setAttribute('title', `Go to ${
                    months[prevMonth.getMonth()]} ${date.getFullYear()}`)
        }

        if (nextBtn) {
            const nextMonth = new Date(year, date.getMonth() + 1, 1)

            if ((!maxDate
                        && nextBtn.disabled)
                    || (maxDate
                        && maxDate.getTime() > nextMonth.getTime()))
                nextBtn.disabled = false

            if (maxDate
                    && maxDate.getTime() < nextMonth.getTime())
                nextBtn.disabled = true

            if (nextBtn.disabled
                    && nextBtn.getAttribute('title'))
                nextBtn.removeAttribute('title')

            if (!nextBtn.disabled)
                nextBtn.setAttribute('title', `${
                    months[nextMonth.getMonth()]} ${date.getFullYear()}`)
        }

        const calendarHeaderLabel = calendar
            .querySelector('.datepicker-calendar-header-label')

        const calendarHeaderLabelText
            = `${months[date.getMonth()]} ${date.getFullYear()}`

        if (calendarHeaderLabel
                && calendarHeaderLabel.innerText !== calendarHeaderLabelText)
            calendarHeaderLabel.innerText = calendarHeaderLabelText

        const monthFirstDay = new Date(year, date.getMonth(), 1)
        const monthLastDay = new Date(year, date.getMonth() + 1, 0)
        const cells = []

        for (let i = 0; i < monthFirstDay.getDay(); i++)
            cells.push('<td class="disabled" tabindex="-1"></td>')

        const maxDateTimestampInt = maxDate
            ? Number(this.getDateTimestamp(maxDate)
                .replace(/\D/g, ''))
            : null

        const minDateTimestampInt = minDate
            ? Number(this.getDateTimestamp(minDate)
                .replace(/\D/g, ''))
            : null

        const inputValueTimestamp = inputValueDate
            ? this.getDateTimestamp(inputValueDate)
            : null

        const today = this.getTodayDate()

        const todayTimestamp = this.getDateTimestamp(today)

        for (let i = 0; i < monthLastDay.getDate(); i++) {
            const timestamp = `${year}-${
                String(date.getMonth() + 1).padStart(2, '0')}-${
                String(i + 1).padStart(2, '0')}`

            const timestampInt = Number(timestamp.replace(/\D/g, ''))

            const isDisabled = (maxDateTimestampInt
                    && timestampInt > maxDateTimestampInt)
                || (minDate
                    && timestampInt < minDateTimestampInt)

            const isSelected = !isDisabled
                && inputValueTimestamp === timestamp

            const isToday = todayTimestamp === timestamp

            const isTabIndex = isSelected
                || (!inputValueTimestamp
                    && isToday)
                || (!isSelected
                    && today.getMonth() !== date.getMonth()
                    && !i)

            const classname = `${
                isDisabled
                    ? 'disabled'
                    : ''}${
                isToday
                    ? ' today'
                    : ''}`
                .trim()

            cells.push(
                `<td
                    tabindex="${isTabIndex ? '0' : '-1'}"
                    class="${classname}"
                    data-date="${timestamp}"
                    ${isSelected ? 'aria-selected="true"' : ''}
                ><span
                    class="${classname}"
                    data-date="${timestamp}"
                >${i + 1}</span></td>`)
        }

        const rows = []

        for (let i = 0; i < cells.length; i++) {
            if (!(i % 7))
                rows[rows.length] = []

            rows[rows.length - 1].push(cells[i])
        }

        const lastRow = rows[rows.length - 1]
        const lastRowDisabledLength = 7 - lastRow.length

        if (lastRowDisabledLength)
            for (let i = 0; i < lastRowDisabledLength; i++)
                lastRow.push('<td class="disabled" tabindex="-1"></td>')

        const calendarTbody = calendar.querySelector('tbody')

        if (calendarTbody)
            calendarTbody.innerHTML = rows
                .map(arr => `<tr>${arr.join('')}</tr>`)
                .join('')
    }

    render () {
        if (!this.getDatepickerInput().id)
            this.getDatepickerInput().id = `datepicker-${this.generateGUID()}`

        this.renderIcon()

        // Use native date picker on touch
        if (this.getFeature('touch')) {
            if (this.getDatepickerInput().value)
                this.getDatepickerInput()
                    .classList.remove('datepicker-input-empty')

            if (!this.getDatepickerInput().value)
                this.getDatepickerInput()
                    .classList.add('datepicker-input-empty')

            if (this.getDatepickerInput().type !== 'date') {
                this.getDatepickerInput().removeAttribute('pattern')
                this.getDatepickerInput().removeAttribute('placeholder')
                this.getDatepickerInput().setAttribute('type', 'date')
                this.renderTouchPlaceholder()
            }
        }

        const maxDate = this.getMaxDate()
        const minDate = this.getMinDate()

        if (maxDate
                && this.getDatepickerInput().type === 'date'
                && this.getDatepickerInput().getAttribute('max')
                    !== this.getDateTimestamp(maxDate))
            this.getDatepickerInput()
                .setAttribute('max', this.getDateTimestamp(maxDate))

        if (minDate
                && this.getDatepickerInput().type === 'date'
                && this.getDatepickerInput().getAttribute('min')
                    !== this.getDateTimestamp(minDate))
            this.getDatepickerInput()
                .setAttribute('min', this.getDateTimestamp(minDate))

        const rangeStartInput = this.getDatepickerRangeStartInput()
        const rangeEndInput = this.getDatepickerRangeEndInput()

        if (this.isRangeStart() && rangeEndInput) {
            const minRangeEnd = this.getDatepickerInput().value

            const rangeEndDatepicker = rangeEndInput
                .parentElement
                .parentElement

            if (!minRangeEnd) {
                const minInit = rangeEndDatepicker
                    .getAttribute('data-min-date-init')

                const min = rangeEndDatepicker
                    .getAttribute('data-min-date')

                if (!minInit && min) {
                    rangeEndInput.removeAttribute('min')
                    rangeEndDatepicker.removeAttribute('data-min-date')
                }

                if (min !== minInit) {
                    if (rangeEndInput.type === 'date')
                        rangeEndInput
                            .setAttribute('min', minInit)

                    rangeEndDatepicker
                        .setAttribute('data-min-date', minInit)
                }
            }

            if (minRangeEnd
                    && rangeEndDatepicker.getAttribute('data-min-date')
                        !== minRangeEnd) {
                if (rangeEndInput.type === 'date')
                    rangeEndInput
                        .setAttribute('min', minRangeEnd)

                rangeEndDatepicker
                    .setAttribute('data-min-date', minRangeEnd)
            }
        }

        if (this.isRangeEnd() && rangeStartInput) {
            const maxRangeStart = this.getDatepickerInput().value

            const rangeStartDatepicker = rangeStartInput
                .parentElement
                .parentElement

            if (!maxRangeStart) {
                const maxInit = rangeStartDatepicker
                    .getAttribute('data-max-date-init')

                const max = rangeStartDatepicker
                    .getAttribute('data-max-date')

                if (!maxInit && max) {
                    rangeStartInput.removeAttribute('max')
                    rangeStartDatepicker.removeAttribute('data-max-date')
                }

                if (max !== maxInit) {
                    if (rangeStartInput.type === 'date')
                        rangeStartInput
                            .setAttribute('max', maxInit)

                    rangeStartDatepicker
                        .setAttribute('data-max-date', maxInit)
                }
            }

            if (maxRangeStart
                    && rangeStartDatepicker.getAttribute('data-max-date')
                        !== maxRangeStart) {
                if (rangeStartInput.type === 'date')
                    rangeStartInput
                        .setAttribute('max', maxRangeStart)

                rangeStartDatepicker
                    .setAttribute('data-max-date', maxRangeStart)
            }
        }

        this.renderCalendar()

        if (!this.focusOnRender)
            return

        let el

        if (typeof this.focusOnRender === 'string')
            el = this.el.querySelector(this.focusOnRender)

        if (typeof this.focusOnRender === 'function')
            el = this.focusOnRender()

        this.focusOnRender = null

        if (el && el.tagName === 'TD') {
            Array.from(this.el.querySelectorAll('td[tabindex="0"]'))
                .filter(_el => _el !== el)
                .forEach(_el => _el.removeAttribute('tabindex'))

            el.setAttribute('tabindex', 0)
        }

        if (el)
            el.focus()
    }
}
